import {
  Dialog,
  FileInputButton,
  Button,
  FormField,
  FormGrid,
  FormRow,
} from "@endya-conformite/ui-kit";
import DownloadIcon from "@endya-conformite/ui-kit/src/assets/icons/download.svg?react";
import Warning from "@endya-conformite/ui-kit/src/assets/icons/exclamation-circle-fill.svg?react";
import Info from "@endya-conformite/ui-kit/src/assets/icons/info-circle-fill.svg?react";

import {
  downloadSheetFromWorkbook,
  importWorkBookFromFile,
} from "@src/helper/excel/excel.export.helper";
import { useMe } from "@src/store/store.me";
import classNames from "classnames";
import { useState } from "react";
import {
  masquerColonnesAxesOptionnels,
  useAxesOptionnelsDisponibles,
} from "../axesOptionnelsExcel";
import styles from "./ImportAssujettiDialog.module.scss";

type WarningContentProps = {
  onCancel: () => void;
  onConfirm: (cleanOnImport: boolean) => void;
  defaultValue: "complete" | "replace";
};

const WARNING_CONTENT_FORM_ID = "warning-content-form-id";
function WarningContent({
  onCancel,
  onConfirm,
  defaultValue,
}: WarningContentProps) {
  const [selected, setSelected] = useState<"complete" | "replace">(
    defaultValue
  );

  return (
    <FormGrid>
      <FormRow>
        <FormField>
          <h3 className={styles.title}>Je souhaite que cet import :</h3>
        </FormField>
      </FormRow>

      <FormRow className={styles.completeButton}>
        <button
          className={styles.button}
          onClick={() => setSelected("complete")}
        >
          <div
            className={classNames(styles.card, {
              [styles.selected]: selected === "complete",
            })}
          >
            <p>Complète la liste existante des assujettis à contrôler</p>
            <div className={classNames(styles.blue, styles.text)}>
              <Info />
              <p>
                Les assujettis dans cet import s’ajouteront aux assujettis déjà
                renseignées.
              </p>
            </div>
          </div>
        </button>
      </FormRow>
      <FormRow className={styles.replaceButton}>
        <button
          className={styles.button}
          onClick={() => setSelected("replace")}
        >
          <div
            className={classNames(styles.card, {
              [styles.selected]: selected === "replace",
            })}
          >
            <p>Remplace la liste existante des assujettis</p>
            <div className={classNames(styles.orange, styles.text)}>
              <Warning />
              <p>Les données actuelles seront écrasées</p>
            </div>
          </div>
        </button>
      </FormRow>

      <FormRow>
        <FormField className={styles.actions}>
          <Button variant="text" onClick={onCancel}>
            Annuler
          </Button>
          <Button
            form={WARNING_CONTENT_FORM_ID}
            onClick={() => onConfirm(selected === "replace")}
            variant="filled"
          >
            Confirmer
          </Button>
        </FormField>
      </FormRow>
    </FormGrid>
  );
}

const MODELE_PERSONNES_ASSUJETTIES = "modele_personnes_assujetties.xlsx";

const telechargerModeleSansAxesOptionnels = async () => {
  const workbook = await importWorkBookFromFile(MODELE_PERSONNES_ASSUJETTIES);
  masquerColonnesAxesOptionnels(workbook.worksheets[0]);
  await downloadSheetFromWorkbook(MODELE_PERSONNES_ASSUJETTIES, workbook);
};

type ImportContentProps = {
  onCancel: () => void;
  onFileChange: (file: File | null | undefined, clean: boolean) => void;
  onClose?: (value: string) => void;
  cleanOnImport: boolean;
};

function ImportContent({
  onCancel,
  onFileChange,
  onClose,
  cleanOnImport,
}: ImportContentProps) {
  const { me } = useMe();
  const axesOptionnelsDisponibles = useAxesOptionnelsDisponibles();

  if (!me) return <div />;
  return (
    <FormGrid>
      <FormRow>
        <FormField>
          <h3>Importer les assujettis en masse</h3>
        </FormField>
      </FormRow>
      <FormRow>
        <FormField>
          <p>
            Renseignez toutes les informations des personnes dans un fichier
            excel à partir d&apos;un modèle téléchargeable
          </p>
        </FormField>
      </FormRow>
      <FormRow>
        <FormField>
          {axesOptionnelsDisponibles ? (
            <a
              href={`/${MODELE_PERSONNES_ASSUJETTIES}`}
              target="_blank"
              rel="noreferrer"
              className={styles.downloadLink}
            >
              <Button
                variant="text"
                icon={<DownloadIcon />}
                className={styles.download}
              >
                Télécharger le modèle
              </Button>
            </a>
          ) : (
            <Button
              variant="text"
              icon={<DownloadIcon />}
              className={styles.download}
              onClick={() => {
                telechargerModeleSansAxesOptionnels().catch(console.error);
              }}
            >
              Télécharger le modèle
            </Button>
          )}
        </FormField>
      </FormRow>
      <FormRow>
        <FormField className={styles.actions}>
          <Button variant="text" onClick={onCancel}>
            Précédent
          </Button>
          <FileInputButton
            onChange={(e) => {
              onFileChange(e.target.files?.item(0), cleanOnImport);
              onClose?.("true");
            }}
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          >
            Importer le fichier complété
          </FileInputButton>
        </FormField>
      </FormRow>
    </FormGrid>
  );
}

export type ImportAssujettiDialogProps = {
  onClose?: (value: string) => void;
  dialogRef: React.MutableRefObject<HTMLDialogElement | null>;
  onFileChange: (file: File | null | undefined, clean: boolean) => void;
};
export function ImportAssujettiDialog({
  onClose,
  dialogRef,
  onFileChange,
}: ImportAssujettiDialogProps) {
  const [state, setState] = useState<{
    step: "warning" | "import";
    cleanOnImport: boolean;
  }>({ step: "warning", cleanOnImport: false });
  return (
    <Dialog dialogRef={dialogRef}>
      {state.step === "warning" && (
        <WarningContent
          onCancel={() => onClose?.("false")}
          onConfirm={(shouldClean) =>
            setState({
              step: "import",
              cleanOnImport: shouldClean,
            })
          }
          defaultValue={state.cleanOnImport ? "replace" : "complete"}
        />
      )}
      {state.step === "import" && (
        <ImportContent
          onCancel={() => {
            setState((prev) => ({ ...prev, step: "warning" }));
          }}
          onFileChange={onFileChange}
          onClose={onClose}
          cleanOnImport={state.cleanOnImport}
        />
      )}
    </Dialog>
  );
}
